import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/** Which Check Your Records card a group is on (PD-16647). */
export type LicenseDuplicateReviewKind = 'duplicate' | 'shared';

/**
 * What the customer decided about a Duplicate License / Shared License group.
 * A plain varchar, like `client_license_compliance_review.disposition`, so a
 * later outcome is an additive change the database sync can carry.
 *
 *   fine_as_is  — "Fine as is". The group is legitimate (a travelling
 *                 veterinarian's license on several clinics, one ERP record
 *                 per ship-to). Hidden until the group's SET OF LICENSE
 *                 RECORDS changes — a copy or an account added, removed or
 *                 re-pointed — measured by `member_fingerprint`. Expiration or
 *                 status drift does not reopen it (Chris, 2026-09-28).
 *   being_fixed — "Being fixed". The customer is correcting it in their ERP.
 *                 Hidden for 14 days (`hidden_until`), then back if the group
 *                 still exists.
 *   reopened    — the undo of the decision before it, written by the Reopen
 *                 action as a new row (the table is insert-only). Never hides.
 */
export type LicenseDuplicateReviewDisposition =
	| 'fine_as_is'
	| 'being_fixed'
	| 'reopened';

/** The outcomes a person records; `reopened` is only written by Reopen. */
export type LicenseDuplicateReviewDecision = Exclude<
	LicenseDuplicateReviewDisposition,
	'reopened'
>;

/** Display wording for each disposition — the lists, the export and the modal. */
export const LICENSE_DUPLICATE_REVIEW_DISPOSITION_LABELS: Record<
	LicenseDuplicateReviewDisposition,
	string
> = {
	fine_as_is: 'Fine as is',
	being_fixed: 'Being fixed',
	reopened: 'Re-opened',
};

/** How long "Being fixed" hides a group. */
export const LICENSE_DUPLICATE_REVIEW_BEING_FIXED_DAYS = 14;

/**
 * One recorded decision about one Duplicate License / Shared License GROUP
 * (PD-16653) — the Compliance "Check Your Records" mark-off.
 *
 * Modeled on `client_license_compliance_review` (the Adverse Action review):
 * INSERT-ONLY, the newest non-deleted row per `group_key` is the decision, a
 * note is mandatory, and who and when are recorded. Two differences, because
 * the subject is a group of license rows rather than one:
 *
 *   1. The key is the GROUP's (`group_key`, computed by
 *      `findDuplicateLicenseCandidates` in libs/shared), not a license id, and
 *      the decision binds to the group's MEMBERSHIP: `member_fingerprint` is
 *      the SHA-256 of the sorted member license ids, and the member ids
 *      themselves are kept (`member_license_ids`) so a decision about a group
 *      that later vanishes (the customer fixed the ERP) still says what it
 *      covered. No foreign key to a license for the same reason.
 *   2. The note is a column here, not a `license_note`: it is about the group,
 *      so one note per decision, not a copy on every member license (a bulk
 *      mark-off of 100 groups would otherwise write several hundred notes).
 *      Each member license's own history still records the decision, with the
 *      note, as a `license_activity` row.
 */
@Index('idx_cldr_group_key', ['groupKey', 'reviewedAt'], {})
@Index('idx_cldr_kind', ['kind', 'reviewedAt'], {})
@Entity('client_license_duplicate_review')
export class LicenseDuplicateReviewEntity extends BaseEntity {
	/** The group's key, e.g. `S|DEA||BE7893921` or `D|2887|STATE BOARD|NC|14192`. */
	@Column('varchar', { name: 'group_key', length: 255 })
	public groupKey!: string;

	@Column('varchar', { name: 'kind', length: 20 })
	public kind!: LicenseDuplicateReviewKind;

	@Column('varchar', { name: 'disposition', length: 50 })
	public disposition!: LicenseDuplicateReviewDisposition;

	/**
	 * SHA-256 (hex) of the group's member `client_license._id`s, ascending and
	 * comma-joined, as reviewed. "Fine as is" hides the group only while the
	 * live group has this exact fingerprint.
	 */
	@Column('char', { name: 'member_fingerprint', length: 64 })
	public memberFingerprint!: string;

	/** The member license ids as reviewed, ascending — the fingerprint's input. */
	@Column('json', { name: 'member_license_ids' })
	public memberLicenseIds!: Array<number>;

	/** "Being fixed": the day the group comes back (review day + 14). */
	@Column('date', { name: 'hidden_until', nullable: true })
	public hiddenUntil!: string | null;

	/** The mandatory note (optional only on a Reopen). */
	@Column('text', { name: 'note', nullable: true })
	public note!: string | null;

	/**
	 * NON-BINDING forensics: the group as the reviewer saw it (label, reason,
	 * accounts, whether it was a bulk decision). Nothing here takes part in
	 * whether the decision hides the group.
	 */
	@Column('json', { name: 'reviewed_context', nullable: true })
	public reviewedContext!: Record<string, unknown> | null;

	/** Tenant `user._id` of the person who decided. */
	@Column('int', {
		name: 'reviewed_by_user_id',
		unsigned: true,
		nullable: true,
	})
	public reviewedByUserId!: number | null;

	@Column('datetime', { name: 'reviewed_at' })
	public reviewedAt!: Date;
}
